import type { ColumnDefinition } from './table.js';

/**
 * Creates a type-safe style getter function for CSS modules.
 * Throws an error if a requested style key doesn't exist.
 */
export const createStyleGetter = <T extends Record<string, string | undefined>>(
  styles: T
) => {
  return (className: keyof T): string => {
    const value = styles[className];

    if (value === undefined) {
      throw new Error(`Style key ${String(className)} not found`);
    }

    return value;
  };
};

export const getNestedValue = (obj: unknown, path: string): unknown => {
  if (!path) {
    return obj;
  }

  const keys = path.split('.');
  let value: unknown = obj;

  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }

    value = (value as Record<string, unknown>)[key];
  }

  return value;
};

export const getCellValue = <TRow>(
  row: TRow,
  column: ColumnDefinition<TRow>
): unknown => {
  if (column.valueGetter) {
    return column.valueGetter(row);
  }

  if (column.field) {
    return getNestedValue(row, column.field);
  }

  return undefined;
};

export const formatCellValue = <TRow>(
  row: TRow,
  column: ColumnDefinition<TRow>
): string => {
  const value = getCellValue(row, column);

  if (column.valueFormatter) {
    return column.valueFormatter(value);
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  // At this point, value must be a primitive (string, number, boolean, symbol, bigint)
  return String(value as string | number | boolean | symbol | bigint);
};
